/* tslint:disable:no-small-switch */
import {
  PlacementCSData,
} from '../../models/placement-data.model';
import { Analytics } from '../../modules/analytics/analytics';
import {
  BpMetricsClickPlacementProperties,
  BpMetricsViewPlacementProperties
} from '@ads-ep/api-metrics';
import { autoInjectable } from 'tsyringe';
import { BaseOverlayComponent } from '../overlays/base-overlay/base-overlay.component';
import { BpOverlayComponentConstructor } from '../../models/component.model';
import { OverlayService } from '../../services/overlay/overlay.service';
import { GenericOverlayComponent } from '../overlays/generic-brand/generic-overlay.component';
import { PlacementConfig } from '../../models/placement-config.model';
import { AnalyticsActions, OverlayActions } from '../../actions';
import { placementActionReducer } from '../../actions/placement-action-reducer';
import { PlacementMetadata } from '../../models/placement-meta-data.model';
import { EpUtil } from '../../util/ep-util';
import { fromEvent, merge, Observable, Subscription } from 'rxjs';
import { exhaustMap, filter, map, mergeMap, tap } from 'rxjs/operators';
import { fromPromise } from 'rxjs/internal-compatibility';
import { GeneratePlacementResponse, SSRTextPlacementContentContentTypeEnum } from '@ads-ep/fetch-api-client';
import { isSSRContent } from '../../util/content-guards.util';
import { cleanMetricsContent } from '../../modules/analytics/util/ep-metrics-event.util';
import { CommonPlacement } from '../common-placement/common-placement.component';
import { EpStore } from '../../services/store/ep-store';

/*
 * This is basically an abstract class and not meant to be
 * instantiated directly. Each placement extends this base
 * component.
 * */
@autoInjectable()
export class BaseTextPlacementComponent extends CommonPlacement{
  selector: string;
  templateFn: (...args: any[]) => string;
  overlayClass: BpOverlayComponentConstructor<BaseOverlayComponent> = GenericOverlayComponent;
  templateData: any;
  placementMetaData: PlacementMetadata;
  clickActions$: Observable<MouseEvent>;
  /*
  * Component specific view object to be used in the
  * component template's render function. Use cases include
  * calculating the monthly price and using it to replace
  * a mustache variable set in a content string from the
  * server.
  * */
  componentTemplateData: any = {};

  /*
   * Default placement data to use if none provided
   * */
  defaultPlacementData: PlacementCSData = {};

  constructor(
    public domTarget: HTMLElement,
    public placementConfig: PlacementConfig = new PlacementConfig(),
    public brandPresentmentConfig?: GeneratePlacementResponse,
    private analytics?: Analytics,
    private overlayService?: OverlayService,
    private store?: EpStore,
  ) {
    super();
    this.placementConfig = EpUtil.returnCopy(placementConfig);
  }

  /*
   * Run after component is created. Code to bind
   * the component to the DOM and add event handlers
   * should go here.
   * */
  init() {
    if(isSSRContent(this.placementConfig.content)){
      this.domTarget.innerHTML = this.placementConfig.content.contentData.htmlContent;
    } else if (!!this.templateFn) {
      // @ts-ignore
      this.templateData = {...this.placementConfig.content.contentData};
      this.domTarget.innerHTML = this.templateFn(this.templateData);
    }
    this.bindActions(this.domTarget);

    /*
    * Store brand provided placement meta data
    * */
    this.placementMetaData = new PlacementMetadata(this.domTarget.dataset);
    this.trackViewPlacementEvent();
  }

  /*
  * Track the view placement event
  * */
  trackViewPlacementEvent(){

    const eventData: BpMetricsViewPlacementProperties = {
      eventProperties: {
        placement: this.analytics.getPlacementTrackingObj(this.placementConfig),
        placementContent: cleanMetricsContent(this.placementConfig.content),
        metadata: this.placementMetaData,
      },
      userProperties: {}
    };
    // Clean placement tracking data
    this.store.dispatch(AnalyticsActions.trackViewPlacementEvent({eventData}))
  }

  /*
 * Parse injected dom for data-action-id attributes.
 * If found, bind a click handler to them to call performPlacementAction
 * */
  bindActions(domTarget: HTMLElement){
    /*
    * Should there be a flag or check to ensure listeners aren't added twice?
    * */
    const actionTargets = domTarget.querySelectorAll(this.actionQuerySelector);

    if(actionTargets.length === 0){
      return;
    }

    this.clickActions$ = fromEvent<MouseEvent>(actionTargets, 'click');
    this.enterKeyActions$ = fromEvent<KeyboardEvent>(actionTargets, 'keyup').pipe(filter((event) => event.code === 'Enter'));
    this.placementActions$ = merge(this.clickActions$,this.enterKeyActions$);

    this.placementActions$.pipe(
      map(e => e.currentTarget),
      filter((domEl: HTMLElement)=>!!domEl.dataset.actionType),
      tap(domEl=> this.store.dispatch(
        AnalyticsActions.trackPlacementClickEvent({
          domEl,
          placementConfig: this.placementConfig,
          placementMetaData: this.placementMetaData,
        }))
      ),
      map(domEl=> placementActionReducer(
        domEl.dataset.actionType,
        domEl.dataset,
        this
      )),
      filter(action=> !!action),
      tap(action=> this.store.dispatch(action))
    ).subscribe();
  }
}
